import { useHistory } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { Header } from "@/react/components/Chat/MessagingV2/RailBar/Header";
import { chatEventsEmitter } from "@/react/hooks/chatsV2/chatEventsEmitter";
import { RailBarPortal } from "@circle-react/components/Layout/StandardLayoutV2/RailBarPortal";
import { newChatRoomPath } from "@circle-react/helpers/urlHelpers";
import { useCommunityInboxStore } from "@circle-react/hooks/chatsV2/store";
import { useElementHeight } from "@circle-react/hooks/utils/useElementHeight";
import type { Profile } from "@circle-react/types/CommunityBot/Profile";
import { Icon } from "@circle-react-shared/Icon";
import { TiptapEditor } from "@circle-react-shared/uikit/TipTap";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { Rail } from "@circle-react-uikit/Rail";
import { SuggestedQuestionButton } from "../Shared/SuggestedQuestionButton";
import { AgentAvatar } from "./AgentAvatar";
import { PrivacyNotice } from "./PrivacyNotice";

interface BotProfileRailbarProps {
  botProfile: Profile;
  conversationStatus?: "getting_too_long" | "too_long" | "exceeded" | "normal";
}

export const BotProfileRailbar = ({
  botProfile,
  conversationStatus = "normal",
}: BotProfileRailbarProps) => {
  const { ref: headerRef, height: headerHeight } = useElementHeight();
  const history = useHistory();
  const { setCurrentBotProfile } = useCommunityInboxStore();

  const handleNewChat = () => {
    setCurrentBotProfile(botProfile);
    history.push({
      pathname: newChatRoomPath(),
    });
  };

  const formatMessageBody = (text: string) => ({
    type: "doc",
    content: [
      {
        type: "paragraph",
        content: [
          {
            type: "text",
            text: text,
          },
        ],
      },
    ],
  });

  const handleSendSuggestion = (question: string) => {
    const payload = {
      body: {
        body: formatMessageBody(question),
        attachments: [],
        localAttachments: [],
        localSgidToObjectMap: {},
      },
    };
    chatEventsEmitter.sendSuggestedQuestion(payload);
  };

  return (
    <RailBarPortal>
      <div ref={headerRef}>
        <Rail.Header className="!min-h-14">
          <Header title={t("profile")} isDirect />
        </Rail.Header>
      </div>
      <Rail.Content noPadding heightGap={headerHeight}>
        <div className="flex h-full flex-col px-6 py-5">
          <div className="flex grow flex-col">
            <div className="mb-4">
              <AgentAvatar profile={botProfile} />
              <Typography.TitleSm weight="semibold">
                {botProfile.name}
              </Typography.TitleSm>
            </div>
            {botProfile.enabled && botProfile.allow_direct_messages && (
              <div className="mb-4">
                <button
                  type="button"
                  className="border-primary hover:bg-tertiary text-secondary flex items-baseline whitespace-normal rounded-[1.25rem] border px-3.5 py-2 text-left text-sm font-medium"
                  aria-label={t("chat_space.header.new_conversation")}
                  onClick={handleNewChat}
                >
                  <Icon
                    aria-label={t("chat_space.header.new_conversation")}
                    type="new-conversation"
                    className="text-dark mr-1"
                    useWithFillCurrentColor
                    size={20}
                  />
                  <Typography.LabelSm weight="medium">
                    {t("chat_space.header.new_conversation")}
                  </Typography.LabelSm>
                </button>
              </div>
            )}
            {botProfile.bio && (
              <div className="mb-5 flex flex-col gap-y-2">
                <Typography.LabelSm weight="semibold">
                  {t("community_inbox.profile.bio")}
                </Typography.LabelSm>
                <div className="text-sm font-normal leading-5 tracking-tighter">
                  <TiptapEditor
                    editable={false}
                    rich_text_body={botProfile.bio}
                    shouldShowPreviewBlocks={false}
                  />
                </div>
              </div>
            )}
            {botProfile.suggested_questions &&
              botProfile.suggested_questions.length > 0 && (
                <div className="mb-5 flex flex-col gap-y-2">
                  <Typography.LabelSm weight="semibold">
                    {t("community_bot.profiles.suggested_questions_label")}
                  </Typography.LabelSm>
                  <div className="flex flex-col items-start gap-2">
                    {botProfile.suggested_questions.map((question: string) => (
                      <SuggestedQuestionButton
                        key={question}
                        question={question}
                        onClick={() => handleSendSuggestion(question)}
                        disabled={
                          !botProfile.enabled ||
                          !botProfile.allow_direct_messages ||
                          conversationStatus === "too_long" ||
                          conversationStatus === "exceeded"
                        }
                      />
                    ))}
                  </div>
                </div>
              )}
          </div>
          <div className="shrink-0 pb-2 text-center">
            <PrivacyNotice />
          </div>
        </div>
      </Rail.Content>
    </RailBarPortal>
  );
};
